import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import SEOHead from '../../components/feature/SEOHead';

const PHONE = '07760 881212';
const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

const ABOUT_OG_IMAGE = 'https://public.readdy.ai/ai/img_res/562be120d25489bd9b4bd66181ff789d.jpg';

const values = [
  {
    icon: 'ri-shield-check-fill',
    title: 'Honest & Transparent',
    desc: 'No hidden charges, no confusing jargon. We give you a clear price before we start any work and we stick to it.',
  },
  {
    icon: 'ri-time-fill',
    title: 'Reliable & On Time',
    desc: 'We turn up when we say we will. Your time is valuable, and we treat it that way — no vague four-hour windows.',
  },
  {
    icon: 'ri-home-heart-fill',
    title: 'Tidy & Respectful',
    desc: 'We treat every home as if it were our own. We clean up fully before we leave — you\'d barely know we\'d been there.',
  },
  {
    icon: 'ri-flashlight-fill',
    title: 'Fully Qualified',
    desc: 'All work is carried out to the highest standard and certified on completion. We\'re NAPIT registered and Part P registered.',
  },
  {
    icon: 'ri-map-pin-fill',
    title: 'Proudly Local',
    desc: 'We\'re based right here in Worksop. We know the area, we know the housing stock, and we\'re part of this community.',
  },
  {
    icon: 'ri-customer-service-fill',
    title: 'Responsive & Reachable',
    desc: 'We do our best to respond quickly and keep you informed at every stage. If you call, we\'ll pick up or call you back promptly.',
  },
];

const qualifications = [
  { icon: 'ri-award-fill', label: 'NAPIT Registered' },
  { icon: 'ri-file-certificate-fill', label: 'Part P Registered' },
  { icon: 'ri-shield-fill', label: 'Fully Insured — Public Liability' },
  { icon: 'ri-graduation-cap-fill', label: 'City & Guilds Qualified' },
  { icon: 'ri-verified-badge-fill', label: 'Trustmark Approved' },
  { icon: 'ri-home-fill', label: 'Domestic Installer Qualified' },
  { icon: 'ri-fire-fill', label: 'Fire Alarm Inspections Certified' },
];

const aboutJsonLd = [
  {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    '@id': `${siteUrl}/about#webpage`,
    url: `${siteUrl}/about`,
    name: 'About Us — Local Electrician Worksop | Trusted & Qualified',
    description:
      'Get to know your local electrician in Worksop. Fully qualified, NAPIT registered, and proudly serving Worksop, Retford, and surrounding areas with honest, reliable electrical services.',
    isPartOf: { '@id': `${siteUrl}/#website` },
    about: { '@id': `${siteUrl}/#business` },
    inLanguage: 'en-GB',
  },
  {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: `${siteUrl}/` },
      { '@type': 'ListItem', position: 2, name: 'About Us', item: `${siteUrl}/about` },
    ],
  },
  {
    '@context': 'https://schema.org',
    '@type': 'Electrician',
    '@id': `${siteUrl}/#business`,
    name: 'Dukeries Electrical',
    url: siteUrl,
    telephone: '07760 881212',
    description:
      'Dukeries Electrical — owner-operated electrical business based in Worksop, Nottinghamshire. NAPIT registered, Part P registered. Serving Worksop, Retford, Carlton in Lindrick and Shireoaks.',
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Worksop',
      addressRegion: 'Nottinghamshire',
      postalCode: 'S80',
      addressCountry: 'GB',
    },
    hasCredential: [
      { '@type': 'EducationalOccupationalCredential', credentialCategory: 'NAPIT Registered' },
      { '@type': 'EducationalOccupationalCredential', credentialCategory: 'Part P Registered' },
      { '@type': 'EducationalOccupationalCredential', credentialCategory: '18th Edition Wiring Regulations' },
      { '@type': 'EducationalOccupationalCredential', credentialCategory: 'City & Guilds Qualified' },
      { '@type': 'EducationalOccupationalCredential', credentialCategory: 'Trustmark Approved' },
    ],
    areaServed: [
      { '@type': 'City', name: 'Worksop' },
      { '@type': 'City', name: 'Retford' },
      { '@type': 'Place', name: 'Carlton in Lindrick' },
      { '@type': 'Place', name: 'Shireoaks' },
    ],
    openingHoursSpecification: {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
      opens: '00:00',
      closes: '23:59',
    },
  },
];

export default function AboutPage() {
  return (
    <>
      <SEOHead
        title="About Dukeries Electrical | Local Electrician Worksop | Trusted & Qualified"
        description="Get to know Dukeries Electrical, your local electrician in Worksop. Fully qualified, NAPIT registered, and proudly serving Worksop, Retford, and surrounding areas with honest, reliable electrical services."
        keywords="Dukeries Electrical, electrician Worksop, local electrician, NAPIT registered, qualified electrician Nottinghamshire"
        canonical={`${siteUrl}/about`}
        ogTitle="About Dukeries Electrical | Local Electrician Worksop | Trusted & Qualified"
        ogDescription="Get to know Dukeries Electrical, your local electrician in Worksop. Fully qualified, NAPIT registered, and proudly serving Worksop, Retford, and surrounding areas."
        ogUrl={`${siteUrl}/about`}
        ogImage={ABOUT_OG_IMAGE}
        jsonLd={aboutJsonLd}
      />
      <Navbar />

      {/* Hero */}
      <section className="bg-black text-white py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-3xl">
            <span className="inline-block bg-amber-500/20 text-amber-400 text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full mb-5">
              About Us
            </span>
            <h1 className="font-heading font-extrabold text-white mb-4" style={{ fontSize: 'clamp(2rem, 4vw, 3rem)' }}>
              About Dukeries Electrical — Your Local Electrician in Worksop
            </h1>
            <p className="text-gray-400 text-lg leading-relaxed">
              Dukeries Electrical is a small, owner-operated electrical business based in Worksop, Nottinghamshire. We\'ve been keeping homes safe and lights on for homeowners and landlords across the area for years — and we\'re proud of every job we do.
            </p>
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 md:grid-cols-2 gap-14 items-center">
          {/* Image */}
          <div className="w-full h-96 rounded-2xl overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=friendly%20professional%20male%20electrician%20in%20branded%20workwear%20standing%20confidently%20in%20bright%20modern%20UK%20home%20interior%2C%20holding%20electrical%20tools%2C%20smiling%20directly%20at%20camera%2C%20warm%20natural%20light%2C%20clean%20tidy%20domestic%20setting%20Nottinghamshire%2C%20realistic%20portrait%20photography&width=700&height=600&seq=about-electrician-worksop-portrait&orientation=portrait"
              alt="Local electrician in Worksop"
              className="w-full h-full object-cover object-top"
            />
          </div>

          {/* Text */}
          <div>
            <span className="inline-block bg-amber-100 text-amber-700 text-xs font-bold uppercase tracking-widest px-3 py-1.5 rounded-full mb-5">
              Our Story
            </span>
            <h2 className="font-heading font-extrabold text-navy-800 text-3xl mb-6 leading-tight">
              A Local Business Built on Doing the Job Right
            </h2>
            <div className="space-y-4 text-gray-600 text-sm leading-relaxed">
              <p>
                Dukeries Electrical started out as a one-man operation with a van, a tool bag, and a simple goal: to provide the kind of electrical service we\'d want ourselves. No overcharging, no cutting corners, and no leaving a job until the customer was completely happy.
              </p>
              <p>
                Over the years, that reputation for honest, reliable work has grown through word of mouth across Worksop, Retford, Carlton in Lindrick, and Shireoaks. We\'re proud that the majority of our work still comes from recommendations from existing customers — that tells us we\'re doing something right.
              </p>
              <p>
                We work primarily in domestic properties — houses, flats, bungalows — helping homeowners, landlords, and tenants with everything from fitting a single socket to a complete rewire. Whatever the job, big or small, it gets our full attention and it gets done properly.
              </p>
              <p>
                We\'re NAPIT registered and Part P registered, which means all our work is certified to the current wiring regulations. When we finish a job, you get the paperwork to prove it was done correctly — not just our word for it.
              </p>
            </div>

            <a
              href={`tel:${PHONE.replace(/\s/g, '')}`}
              className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-black font-extrabold text-base px-7 py-3.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap mt-8"
            >
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-phone-fill text-base"></i>
              </span>
              Call Us: {PHONE}
            </a>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <span className="inline-block bg-amber-100 text-amber-700 text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full mb-4">
              What We Stand For
            </span>
            <h2 className="font-heading font-extrabold text-navy-800 text-3xl leading-tight">
              The Values Behind Everything We Do
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {values.map((v) => (
              <div key={v.title} className="bg-white rounded-xl p-7 border border-gray-100">
                <div className="w-12 h-12 flex items-center justify-center bg-amber-500/10 rounded-xl mb-5">
                  <i className={`${v.icon} text-amber-500 text-xl`}></i>
                </div>
                <h3 className="font-heading font-bold text-navy-800 text-lg mb-2">{v.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Qualifications */}
      <section className="py-20 bg-black text-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-14 items-center">
            <div>
              <span className="inline-block bg-amber-500/20 text-amber-400 text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full mb-5">
                Credentials
              </span>
              <h2 className="font-heading font-extrabold text-white text-3xl leading-tight mb-6">
                Qualified, Certified &amp; Fully Insured
              </h2>
              <p className="text-gray-400 text-sm leading-relaxed mb-8">
                We hold all the qualifications and certifications you\'d expect from a professional electrical contractor. Every job we complete is certified in line with current BS 7671 wiring regulations and Building Regulations Part P.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {qualifications.map((q) => (
                  <div key={q.label} className="flex items-center gap-3">
                    <div className="w-9 h-9 flex items-center justify-center bg-amber-500/10 rounded-lg shrink-0">
                      <i className={`${q.icon} text-amber-400 text-base`}></i>
                    </div>
                    <span className="text-gray-300 text-sm font-medium">{q.label}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Image */}
            <div className="w-full h-80 rounded-2xl overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=NICEIC%20certificate%20and%20electrical%20qualification%20documents%20laid%20out%20on%20desk%20with%20hard%20hat%20and%20tool%20belt%2C%20professional%20electrical%20contractor%20credentials%2C%20dark%20dramatic%20lighting%2C%20close%20up%20realistic%20photography&width=700&height=500&seq=about-qualifications-electrician&orientation=landscape"
                alt="Electrician qualifications and certifications"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Service Area */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <span className="inline-block bg-amber-100 text-amber-700 text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full mb-4">
            Where We Work
          </span>
          <h2 className="font-heading font-extrabold text-navy-800 text-3xl mb-4 leading-tight">
            Based in Worksop, Covering the Whole Area
          </h2>
          <p className="text-gray-500 text-sm max-w-2xl mx-auto mb-10">
            We\'re based in Worksop and cover a wide area across North Nottinghamshire and South Yorkshire, including all the towns and villages listed below.
          </p>

          <div className="flex flex-wrap justify-center gap-3 mb-12">
            {['Worksop', 'Retford', 'Carlton in Lindrick', 'Shireoaks', 'Harworth', 'Blyth', 'Ollerton', 'Warsop', 'Mansfield', 'Clowne', 'Whitwell', 'Rhodesia'].map((area) => (
              <span
                key={area}
                className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-full px-4 py-2 text-sm text-navy-800 font-medium"
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-map-pin-fill text-amber-500 text-xs"></i>
                </span>
                {area}
              </span>
            ))}
          </div>

          {/* Map */}
          <div className="w-full h-80 rounded-2xl overflow-hidden border border-gray-200">
            <iframe
              title="Electrician Worksop Service Area Map"
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d76855.2!2d-1.1!3d53.3!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x4879f6c0d3c4b8c1%3A0x1234567890abcdef!2sWorksop%2C%20UK!5e0!3m2!1sen!2suk!4v1234567890"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            ></iframe>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-amber-500 py-14">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h2 className="font-heading font-extrabold text-black text-2xl mb-2">Ready to Book or Get a Quote?</h2>
            <p className="text-black/70 text-base">Give us a call — we\'re friendly, no-pressure, and always happy to chat through what you need.</p>
          </div>
          <a
            href={`tel:${PHONE.replace(/\s/g, '')}`}
            className="flex items-center gap-3 bg-black hover:bg-navy-800 text-amber-400 font-extrabold text-lg px-8 py-4 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
          >
            <span className="w-6 h-6 flex items-center justify-center">
              <i className="ri-phone-fill text-xl"></i>
            </span>
            {PHONE}
          </a>
        </div>
      </section>

      <Footer />
    </>
  );
}
